import { AxiosError, AxiosHeaders, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { apiClient } from './apiClient';
import { extensionOf, isAllowed, uploadRejectionMessage } from './patientFileTypes';
import { SIZE_THRESHOLD, presignAndPutFile } from './uploads.service';
import type { ApiResponse } from '@/types/api.types';

export interface PatientFile {
  name: string;
  type: 'file' | 'folder';
  size: number | null;
  mimeType: string | null;
  modifiedAt: string | null;
  isImage: boolean;
  icon: string;
  source?: string;
}

/**
 * Files and folders are real paths on the docstore rather than rows in a table,
 * so the ceiling is the filesystem's NAME_MAX: 255 bytes per path component.
 * The API enforces the same number; this is the copy that keeps a user from
 * typing past it in the first place.
 */
export const MAX_NAME_BYTES = 255;

/**
 * Counts bytes, not characters: an accented or emoji character spends more than
 * one byte, and bytes are what the filesystem is counting.
 */
export function nameLengthError(name: string, label = 'Name'): string | null {
  if (new TextEncoder().encode(name).length > MAX_NAME_BYTES) {
    return `${label} is too long. Use ${MAX_NAME_BYTES} characters or fewer.`;
  }
  return null;
}

export function listFiles(patientId: number, folder?: string): Promise<PatientFile[]> {
  return apiClient
    .get<ApiResponse<PatientFile[]>>(`/patients/${patientId}/files`, { params: { folder } })
    .then((r) => r.data.data);
}

export function getDownloadUrl(patientId: number, name: string, folder?: string, source?: string): string {
  const params = new URLSearchParams({ name, inline: '1' });
  if (folder) params.set('folder', folder);
  if (source) params.set('source', source);
  return `/patients/${patientId}/files/download?${params.toString()}`;
}

export function getThumbnailUrl(patientId: number, name: string, folder?: string, source?: string): string {
  const params = new URLSearchParams({ name });
  if (folder) params.set('folder', folder);
  if (source) params.set('source', source);
  return `/patients/${patientId}/files/thumbnail?${params.toString()}`;
}

export async function fetchThumbnailBlobUrl(patientId: number, name: string, folder?: string, source?: string): Promise<string | null> {
  try {
    const url = getThumbnailUrl(patientId, name, folder, source);
    const response = await apiClient.get(url, { responseType: 'blob' });
    const blob = response.data as Blob;
    if (blob.size === 0) return null;
    if (blob.type === 'application/json') return null;
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

export async function fetchFileViewUrl(patientId: number, name: string, folder?: string, source?: string): Promise<string | null> {
  try {
    const url = getDownloadUrl(patientId, name, folder, source);
    const response = await apiClient.get(url, { responseType: 'blob' });
    const blob = response.data as Blob;
    if (blob.size === 0) return null;
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

export async function downloadFile(patientId: number, name: string, folder?: string, source?: string): Promise<void> {
  const params = new URLSearchParams({ name, inline: '0' });
  if (folder) params.set('folder', folder);
  if (source) params.set('source', source);
  const response = await apiClient.get(`/patients/${patientId}/files/download?${params.toString()}`, { responseType: 'blob' });
  const blob = response.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** The error the server answers a wrong file type with: 422 VALIDATION_ERROR. */
function typeRefusal(name: string): AxiosError {
  const message = uploadRejectionMessage(extensionOf(name));
  const config = { headers: new AxiosHeaders() } as InternalAxiosRequestConfig;
  const response: AxiosResponse = {
    status: 422,
    statusText: 'Unprocessable Content',
    headers: {},
    config,
    data: { error: { code: 'VALIDATION_ERROR', message } },
  };
  return new AxiosError(message, AxiosError.ERR_BAD_REQUEST, config, undefined, response);
}

export async function uploadFile(
  patientId: number,
  file: File,
  folder?: string,
  onProgress?: (pct: number) => void,
): Promise<{ name: string; folder: string }> {
  // Refused here, before either path below sends a byte, and shaped like the
  // server's own refusal so callers show it the same way.
  if (!isAllowed(file.name)) throw typeRefusal(file.name);

  if (file.size > SIZE_THRESHOLD) {
    onProgress?.(0);
    // Shared helper rather than an inline presign + PUT: the payload arrives
    // wrapped by apiClient's response interceptor, and reading it flat yields an
    // undefined URL that uploads nothing and then posts "undefined" as the key.
    // The purpose lets the API refuse a file type the patient's files do not
    // take before the whole file is sent to storage.
    const s3Key = await presignAndPutFile(file, { purpose: 'patient-files' });
    onProgress?.(90);

    const form = new FormData();
    form.append('s3Key', s3Key);
    if (folder) form.append('folder', folder);
    const res = await apiClient.post<ApiResponse<{ name: string; folder: string }>>(`/patients/${patientId}/files`, form);
    onProgress?.(100);
    return res.data.data;
  }

  const formData = new FormData();
  formData.append('file', file);
  if (folder) formData.append('folder', folder);

  return apiClient
    .post<ApiResponse<{ name: string; folder: string }>>(`/patients/${patientId}/files`, formData, {
      onUploadProgress: onProgress
        ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); }
        : undefined,
    })
    .then((r) => r.data.data);
}

export function deleteFile(
  patientId: number,
  name: string,
  folder?: string,
  source?: string,
  type?: 'file' | 'folder',
  recursive?: boolean,
): Promise<void> {
  return apiClient
    .delete(`/patients/${patientId}/files`, {
      params: { name, folder, source, type, recursive: recursive ? 1 : undefined },
    })
    .then(() => undefined);
}

export function createFolder(patientId: number, name: string, folder?: string): Promise<{ name: string; folder: string }> {
  return apiClient
    .post<ApiResponse<{ name: string; folder: string }>>(`/patients/${patientId}/files/folder`, { name, folder })
    .then((r) => r.data.data);
}

export function renameFile(
  patientId: number,
  name: string,
  newName: string,
  type: 'file' | 'folder',
  folder?: string,
  source?: string,
): Promise<PatientFile> {
  return apiClient
    .patch<ApiResponse<PatientFile>>(`/patients/${patientId}/files/rename`, { name, newName, type, folder, source })
    .then((r) => r.data.data);
}

export function captureCamera(patientId: number, base64Image: string): Promise<{ name: string; source: string }> {
  return apiClient
    .post<ApiResponse<{ name: string; source: string }>>(`/patients/${patientId}/files/camera`, { image: base64Image })
    .then((r) => r.data.data);
}
